import { AccountSuggestion, AccountSuggestionResponse } from './types'

/**
 * Reads one answer from the assistant, and refuses anything it cannot trust.
 *
 * Pure on purpose: this is the only place the answer is believed or not, and it
 * is the part worth testing — the store around it just sends and logs.
 *
 * Three outcomes, and they are different things:
 *   - a suggestion, when the answer named an account that was actually offered;
 *   - neither, when the answer said nothing fits, which is a real answer;
 *   - an error, when the answer cannot be believed. The caller logs those; the
 *     user is never shown one, because a suggestion is an aid and its absence
 *     is not a failure they can act on.
 */
export const readSuggestion = (
  data: string,
  candidateIds: string[],
): { suggestion?: AccountSuggestion, error?: string } => {
  let answer: AccountSuggestionResponse

  try {
    answer = JSON.parse(data) as AccountSuggestionResponse
  } catch {
    return { error: 'The account suggestion was not readable JSON' }
  }

  if (!answer || typeof answer !== 'object') {
    return { error: 'The account suggestion was not an object' }
  }

  // Nothing fitted. There is simply no row to draw for it.
  if (!answer.account_id) return {}

  /*
   * An id we did not offer is dropped. Selecting an account the repository does
   * not have would be worse than having no suggestion at all — and the model
   * cannot check this itself, since the candidates are ours.
   */
  if (!candidateIds.includes(answer.account_id)) {
    return {
      error: `The account suggestion named an account that was not offered: ${answer.account_id}`,
    }
  }

  /*
   * A pick with no grounds is refused too. The row exists to be judged, and
   * "Suggested" on its own is the wizard asserting rather than explaining.
   */
  const reason = answer.reason?.trim()
  if (!reason) {
    return { error: `The account suggestion for ${answer.account_id} carried no reason` }
  }

  return { suggestion: { accountId: answer.account_id, reason } }
}
